export type ConflictStrategy = 'maximum' | 'average' | 'first' | 'last';

// ─── Refusal bounds ──────────────────────────────────────────────────────────
//
// A merge that rewrites a large share of the record is not a set of re-reads —
// it is the signature of a structural mismatch: a duplicate file, a shifted
// offset, or a second meter. `maximum` resolves every one of those silently and
// returns a plausible number, which is how a two-meter VEC export once read as a
// single panel's peak. Past these bounds the merge is refused outright rather
// than resolved behind a success banner.
//
// The absolute floor keeps small files out of it: a handful of DST fall-back
// collisions in a short export must never trip the refusal.
export const MERGE_REFUSAL_MIN_CONFLICTS = 100;
export const MERGE_REFUSAL_CONFLICT_FRACTION = 0.25;

export interface MergeStats {
  totalInputPoints: number;
  totalOutputPoints: number;
  duplicatesRemoved: number;
  conflictsResolved: number;
  strategy: ConflictStrategy;
}

export interface MergeResult {
  data: Array<{ timestamp: string; kw: number }>;
  stats: MergeStats;
}

const STRATEGY_PHRASE: Record<ConflictStrategy, string> = {
  maximum: 'retaining the higher reading',
  average: 'averaging the differing readings',
  first: 'retaining the reading from the earlier file',
  last: 'retaining the reading from the later file',
};

/**
 * AHJ-facing sentence describing what conflict resolution did to this project's
 * interval data. Empty string when nothing was resolved, so the report can omit
 * the section entirely.
 *
 * Deliberately does not claim the data came from more than one file — a single
 * export repeats a timestamp on its own at the daylight-saving fall-back hour.
 * `maximum` is the conservative choice and says so; every other strategy can
 * lower the peak the calculation rests on, and is called out as such.
 */
export function buildMergeDisclosure(
  conflictsResolved: number | null | undefined,
  strategy: string | null | undefined,
): string {
  if (!conflictsResolved || conflictsResolved <= 0) return '';
  const known = (strategy ?? '') as ConflictStrategy;
  const phrase = STRATEGY_PHRASE[known] ?? STRATEGY_PHRASE.maximum;
  const resolved = known in STRATEGY_PHRASE ? known : 'maximum';
  const timestamps = conflictsResolved === 1 ? 'timestamp' : 'timestamps';
  const base =
    `${conflictsResolved.toLocaleString()} ${timestamps} in the interval data carried more than one differing ` +
    `reading. Each was resolved by ${phrase}.`;
  return resolved === 'maximum'
    ? `${base} This is the conservative resolution: the peak demand this calculation rests on is never lowered by it.`
    : `${base} This resolution can return a lower peak than the source data contains, and is disclosed for AHJ review.`;
}

/**
 * True when a completed merge rewrote too much of the record to be trusted as
 * routine conflict resolution. Pure predicate — the caller decides what to do
 * with it; this module never suppresses a merge on its own.
 */
export function shouldRefuseMerge(stats: MergeStats): boolean {
  if (stats.totalOutputPoints === 0) return false;
  if (stats.conflictsResolved < MERGE_REFUSAL_MIN_CONFLICTS) return false;
  return stats.conflictsResolved / stats.totalOutputPoints > MERGE_REFUSAL_CONFLICT_FRACTION;
}

/**
 * Merge multiple AMI datasets into a single sorted dataset.
 *
 * datasets — ordered array of point arrays. For 'first'/'last' strategies,
 *            earlier datasets take lower/higher precedence respectively.
 *            Pass existing project data as datasets[0] so it participates
 *            in deduplication and conflict resolution alongside new files.
 *
 * Deduplication: identical timestamp + identical kw value → keep one, no conflict.
 * Conflict:      identical timestamp + different kw values → apply strategy.
 */
export function mergeAMIDatasets(
  datasets: Array<Array<{ timestamp: string; kw: number }>>,
  strategy: ConflictStrategy
): MergeResult {
  // Build a map: normalized ISO timestamp → ordered list of kw values.
  // Order within each bucket matches dataset order, then insertion order
  // within each dataset — which is what 'first' and 'last' depend on.
  const byTimestamp = new Map<string, number[]>();
  let totalInputPoints = 0;

  for (const dataset of datasets) {
    for (const point of dataset) {
      totalInputPoints++;
      // Normalize to UTC ISO to guard against TZ-offset string differences.
      const key = new Date(point.timestamp).toISOString();
      const bucket = byTimestamp.get(key);
      if (bucket) {
        bucket.push(point.kw);
      } else {
        byTimestamp.set(key, [point.kw]);
      }
    }
  }

  let duplicatesRemoved = 0;
  let conflictsResolved = 0;
  const output: Array<{ timestamp: string; kw: number }> = [];

  for (const [timestamp, values] of byTimestamp) {
    if (values.length === 1) {
      // Unique timestamp — no merge needed.
      output.push({ timestamp, kw: values[0] });
      continue;
    }

    const allSame = values.every(v => v === values[0]);

    if (allSame) {
      // Exact duplicates — drop extras silently.
      duplicatesRemoved += values.length - 1;
      output.push({ timestamp, kw: values[0] });
    } else {
      // Conflict — apply resolution strategy.
      conflictsResolved++;
      let kw: number;
      switch (strategy) {
        case 'maximum':
          kw = Math.max(...values);
          break;
        case 'average':
          kw = values.reduce((a, b) => a + b, 0) / values.length;
          break;
        case 'first':
          kw = values[0];
          break;
        case 'last':
          kw = values[values.length - 1];
          break;
      }
      output.push({ timestamp, kw });
    }
  }

  output.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  return {
    data: output,
    stats: {
      totalInputPoints,
      totalOutputPoints: output.length,
      duplicatesRemoved,
      conflictsResolved,
      strategy,
    },
  };
}
